import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { FlaskConical, BarChart3, Dna, Sparkles, BookOpen, LogOut, Leaf, Box, LineChart, Crown } from 'lucide-react';
import { toast } from 'sonner';

const NAV_ITEMS = [
  { label: 'Designs', path: '/research-design', icon: FlaskConical },
  { label: 'Analysis', path: '/biometrical-analysis', icon: BarChart3 },
  { label: 'Molecular', path: '/molecular-genetics', icon: Dna },
  { label: 'PhenoScan', path: '/phenoscan', icon: Leaf },
  { label: '3D Reconstruction', path: '/3d-reconstruction', icon: Box },
  { label: 'Predict', path: '/predict', icon: LineChart },
  { label: 'Clean', path: '/data-cleaning', icon: Sparkles },
  { label: 'Intelligence', path: '/bk-intelligence', icon: BookOpen },
];

export default function SharedNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/login');
  };

  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <nav
      className="sticky top-0 z-50 bg-white"
      style={{
        height: 64,
        borderBottom: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-nav)',
        backdropFilter: 'blur(12px)',
        backgroundColor: 'rgba(255,255,255,0.95)',
      }}
      data-testid="main-nav"
    >
      <div className="max-w-[1320px] mx-auto px-6 h-full flex items-center justify-between">
        {/* Logo */}
        <div
          className="flex items-center gap-2.5 cursor-pointer select-none"
          onClick={() => navigate('/dashboard')}
          data-testid="nav-logo"
        >
          <Leaf size={22} style={{ color: '#40916C' }} strokeWidth={2.2} />
          <div className="leading-none">
            <span
              className="font-display block"
              style={{ fontSize: 22, fontWeight: 400, color: 'var(--green-900)', letterSpacing: '-0.01em' }}
              data-testid="dashboard-title"
            >
              BKAgriD
            </span>
            <span
              className="font-body block"
              style={{ fontSize: 10.5, color: 'var(--text-light)', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 1, fontWeight: 500 }}
            >
              Biometrical Knowledge
            </span>
          </div>
        </div>

        {/* Nav Links */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`nav-link-underline flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors ${isActive ? 'active' : ''}`}
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  color: isActive ? 'var(--green-900)' : 'var(--text-dark)',
                }}
                data-testid={`${item.label.toLowerCase()}-nav-button`}
              >
                <Icon size={15} style={{ color: isActive ? 'var(--green-500)' : 'var(--text-medium)', transition: 'color 0.2s' }} />
                {item.label}
              </button>
            );
          })}
        </div>

        {/* User Section */}
        <div className="flex items-center gap-3">
          {user?.plan === 'pro' ? (
            <span
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full"
              style={{ background: 'rgba(64,145,108,0.12)', color: '#2D6A4F', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.03em' }}
              data-testid="pro-badge"
            >
              <Crown size={13} /> PRO
            </span>
          ) : (
            <button
              onClick={() => navigate('/pricing')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all btn-press"
              style={{ background: '#40916C', color: 'white', fontSize: 13, fontWeight: 600, border: 'none' }}
              data-testid="nav-upgrade-button"
            >
              <Crown size={14} /> Upgrade
            </button>
          )}
          <div
            className="flex items-center justify-center shrink-0"
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'var(--green-900)',
            }}
            data-testid="user-avatar"
          >
            <span style={{ color: 'white', fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-body)' }}>{initials}</span>
          </div>
          <span
            className="hidden sm:inline font-body"
            style={{ color: 'var(--text-dark)', fontSize: 14, fontWeight: 500 }}
            data-testid="user-name"
          >
            {user?.name}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 transition-colors btn-press"
            style={{
              color: 'var(--text-medium)', fontSize: 13, fontWeight: 500,
              background: 'transparent', border: 'none', padding: '6px 10px',
              borderRadius: 'var(--radius-md)',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--error-red)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-medium)'; }}
            data-testid="logout-button"
          >
            <LogOut size={15} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
